import React, { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';

import NextImage from 'next/image';
import type { ImageProps } from 'next/image';

import getMediaInfo from 'src/utils/getMediaInfo';
import useAudio from 'src/hooks/useAudio';

import { brokenVideo, brokenPendingVideo, transcodingVideo } from 'src/constants/defaultImages';

type VideoProps = Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'heigth' | 'width'> & {
    type?: React.SourceHTMLAttributes<HTMLSourceElement>['type'];
    isTranscoding?: boolean;
    height?: number;
    width?: number;
};
const Video: React.FC<VideoProps> = ({ type, isTranscoding, ...props }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [src, setSrc] = useState<VideoProps['src']>(props.src);
    const [mimeType, setMimeType] = useState<string | undefined>(type);
    const [fallbackImageSrc, setFallbackImageSrc] = useState<ImageProps['src'] | undefined>(undefined);
    const { pause } = useAudio();

    useEffect(() => {
        const stopVideoHandler = (ev: CustomEvent<HTMLVideoElement>) => {
            if (ev.detail !== videoRef.current) {
                videoRef.current?.pause();
            }
        };

        window.addEventListener('stopmedia', stopVideoHandler as EventListener);
        return () => {
            window.removeEventListener('stopmedia', stopVideoHandler as EventListener);
        };
    }, []);

    useEffect(() => {
        setSrc(props.src);
        if (props.src && !type) {
            const mediaInfo = getMediaInfo(props.src);
            setMimeType(mediaInfo.mime);
        }
    }, [props.src, type]);

    const onError = () => {
        let errorMessage;
        const isRemoteVideo = src?.startsWith('http');
        if (isRemoteVideo) {
            if (isTranscoding) {
                errorMessage = `Video download error (transcoding state) : fail to load video with url: ${src}`;
            } else {
                errorMessage = `Video download error : fail to load video with url: ${src}`;
            }
        } else {
            // in case we are adding a new video
            errorMessage = `Video previewSrc error : fail to load video with previewSrc: ${src}`;
        }
        if (process.env.NEXT_PUBLIC_ENV && process.env.NEXT_PUBLIC_ENV !== 'local') {
            Sentry.captureException(new Error(errorMessage));
        }
        let fallbackImageSrcTmp: string | undefined;
        if (isTranscoding) {
            fallbackImageSrcTmp = transcodingVideo;
        } else {
            fallbackImageSrcTmp = isRemoteVideo ? brokenVideo : brokenPendingVideo;
        }
        setFallbackImageSrc(fallbackImageSrcTmp);
    };

    if (fallbackImageSrc) {
        return (
            <NextImage
                src={fallbackImageSrc}
                alt="Broken video"
                width={props.width ? props.width : 1000}
                height={props.height ? props.height : 1000}
                style={{
                    objectFit: 'cover',
                }}
            />
        );
    }
    return src ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
            key={props.src}
            {...props}
            ref={videoRef}
            src={src}
            onError={onError}
            onClick={(e) => e.preventDefault()}
            controls={props.controls}
            onPlay={pause} // Pause Global audio on video play
        >
            <source type={mimeType} />
        </video>
    ) : null;
};

export default Video;
